/**
 * CompaniesHouseCard — CH1 (slice plan v3; Q5: behind "Find on Companies
 * House" on the Company card, records users only). Search Companies House
 * (prefilled with the account's name), pick a company, then see "✦ Suggested
 * from Companies House": each field's suggested value beside the account's
 * current one, applied ONE field per click through the existing account PATCH
 * (history rows server-side). Nothing is fetched until a person searches;
 * nothing is written until a person clicks Apply. Company data only — no
 * officers, no people. Every error sentence is the server's.
 */
import { useState } from 'react'
import { toast } from 'sonner'
import type { CompaniesHouseField, CompaniesHouseSearchItem, CompaniesHouseSuggestion } from '../../../../api/types'
import { searchCompaniesHouse, fetchCompaniesHouseSuggestion, updateAccount, fmtDate } from '../../api'
import { apiErrorMessage } from '../../../../api/client'
import { CH_FIELD_LABEL, chValueWords, chNoteWords, companyStatusWords, chAppliedToast } from './companiesHouseView'

/** What the card remembers across the page's re-read after an Apply (the page
 *  remounts its body while it re-reads; the page owns this ref, so the search,
 *  the results and the picked company survive). */
export interface CompaniesHouseMemory {
  q: string | null
  items: CompaniesHouseSearchItem[] | null
  suggestion: CompaniesHouseSuggestion | null
}
export const emptyCompaniesHouseMemory = (): CompaniesHouseMemory => ({ q: null, items: null, suggestion: null })

export function CompaniesHouseCard({ accountId, accountName, onApplied, onClose, restore, remember }: {
  accountId: string
  accountName: string
  /** Called after each Apply so the page re-reads the account. */
  onApplied: () => void
  onClose: () => void
  /** The page owns the memory (a ref), so a re-read does not lose the card's place: restore reads it once
   *  (the first render's state), remember writes each change into it. */
  restore: () => CompaniesHouseMemory
  remember: (patch: Partial<CompaniesHouseMemory>) => void
}) {
  const [q, setQState] = useState(() => restore().q ?? accountName)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [items, setItemsState] = useState<CompaniesHouseSearchItem[] | null>(() => restore().items)
  const [suggestion, setSuggestionState] = useState<CompaniesHouseSuggestion | null>(() => restore().suggestion)
  const [applying, setApplying] = useState<string | null>(null)
  const setQ = (v: string) => { remember({ q: v }); setQState(v) }
  const setItems = (v: CompaniesHouseSearchItem[] | null) => { remember({ items: v }); setItemsState(v) }
  const setSuggestion = (v: CompaniesHouseSuggestion | null) => { remember({ suggestion: v }); setSuggestionState(v) }

  async function search() {
    setBusy(true); setError(null); setSuggestion(null)
    try {
      const r = await searchCompaniesHouse(accountId, q.trim())
      setItems(r.items ?? [])
    } catch (e) {
      setError(apiErrorMessage(e, 'Companies House search failed'))
      setItems(null)
    } finally { setBusy(false) }
  }

  async function pick(number: string) {
    setBusy(true); setError(null)
    try {
      setSuggestion(await fetchCompaniesHouseSuggestion(accountId, number))
    } catch (e) {
      setError(apiErrorMessage(e, 'Companies House lookup failed'))
    } finally { setBusy(false) }
  }

  async function apply(field: CompaniesHouseField['field'], value: string) {
    const key = `${field}:${value}`
    setApplying(key)
    try {
      await updateAccount(accountId, { [field]: value })
      toast.success(chAppliedToast(field))
      // Refresh the rows FIRST (so "Now:" shows the applied value), then let the page re-read.
      if (suggestion) setSuggestion(await fetchCompaniesHouseSuggestion(accountId, suggestion.companyNumber))
      onApplied()
    } catch (e) {
      toast.error(apiErrorMessage(e, `Could not apply ${CH_FIELD_LABEL[field].toLowerCase()}`))
    } finally { setApplying(null) }
  }

  return (
    <div className="px-5 py-3 border-b border-edge-row" data-testid="companies-house-card">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="text-13 font-semibold text-ink-primary">✦ Suggested from Companies House</div>
        <button type="button" className="k-accounts-add" onClick={onClose}>Close</button>
      </div>
      <form className="flex gap-2 mb-2" onSubmit={e => { e.preventDefault(); void search() }}>
        <input className="flex-1 min-w-0 h-9 px-3 text-12 bg-surface-control border border-edge-control rounded-control text-ink-body placeholder:text-ink-muted focus:outline-none" aria-label="Search Companies House" value={q} maxLength={100}
          onChange={e => setQ(e.target.value)} placeholder="Company name or number" />
        <button type="submit" className="k-btn-secondary" disabled={busy || q.trim() === ''}>Search</button>
      </form>
      {error && <div className="text-12 text-danger py-1" role="alert">{error}</div>}
      {items && !suggestion && (
        items.length === 0
          ? <div className="text-12 text-ink-muted py-1">No companies found at Companies House.</div>
          : <ul className="divide-y divide-edge-row" aria-label="Companies House results">
              {items.map(it => (
                <li key={it.companyNumber} className="flex items-start justify-between gap-3 py-2">
                  <div className="min-w-0">
                    <div className="text-13 text-ink-primary truncate">{it.title}</div>
                    <div className="text-11 text-ink-muted truncate">{[it.companyNumber, companyStatusWords(it.companyStatus), it.address].filter(Boolean).join(' · ')}</div>
                  </div>
                  <button type="button" className="k-btn-secondary flex-shrink-0" disabled={busy}
                    aria-label={`Use ${it.title} (${it.companyNumber})`} onClick={() => { void pick(it.companyNumber) }}>Use</button>
                </li>
              ))}
            </ul>
      )}
      {suggestion && (
        <div>
          <div className="text-11 text-ink-muted mb-1">
            {suggestion.companyName} · {suggestion.companyNumber} · read {fmtDate(suggestion.readAt)}
          </div>
          {(suggestion.fields ?? []).map(f => {
            const note = chNoteWords(f.note)
            const same = f.suggested !== '' && f.suggested === f.current
            const options = f.suggested ? [f.suggested, ...(f.alternatives ?? [])] : (f.note === 'region_straddling' ? (f.alternatives ?? []) : [])
            return (
              <div key={f.field} data-ch-field={f.field} className="py-2 border-b border-edge-row last:border-0">
                <div className="flex items-start justify-between gap-3">
                  <div className="text-12 text-ink-muted w-28 flex-shrink-0">{CH_FIELD_LABEL[f.field]}</div>
                  <div className="min-w-0 flex-1 text-right">
                    <div className="text-11 text-ink-muted whitespace-pre-line break-words">Now: {chValueWords(f.field, f.current) || 'Not set'}</div>
                    {note && <div className="text-12 text-ink-muted">{note}</div>}
                    {options.map(v => (
                      <div key={v} className="flex items-start justify-end gap-2 mt-1">
                        <div className="text-13 text-ink-primary whitespace-pre-line break-words">{chValueWords(f.field, v)}</div>
                        {same && v === f.suggested
                          ? <span className="text-11 text-ink-muted flex-shrink-0">Matches</span>
                          : <button type="button" className="k-btn-secondary flex-shrink-0" disabled={applying !== null}
                              aria-label={`Apply ${CH_FIELD_LABEL[f.field].toLowerCase()}: ${chValueWords(f.field, v).split('\n')[0]}`}
                              onClick={() => { void apply(f.field, v) }}>Apply</button>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )
          })}
          <button type="button" className="k-accounts-add mt-2" onClick={() => setSuggestion(null)}>Back to results</button>
        </div>
      )}
    </div>
  )
}
